import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { UserBoard } from 'src/app/services/models/user-board.model';
import { FilterBar } from 'src/app/services/models/filter-bar.model';
import { SpreadsheetBoardData, SpreadsheetCellType, getCellKey } from 'src/app/services/models/spreadsheet-board.model';
import { CalcEngineService } from 'src/app/services/calc-engine.service';
import { SelectedFilterUpdatesService } from 'src/app/common/bhub-common-module/services/selected-filter-updates.service';
import { CalcEngineRequest, CalcEngineResponse, Metric } from 'src/app/services/models/calc-engine.model';
import { Subscription } from 'rxjs';

@Component({
  selector: 'bhub-spreadsheet-board-viewer',
  templateUrl: './spreadsheet-board-viewer.component.html',
  styleUrls: ['./spreadsheet-board-viewer.component.scss'],
  standalone: false
})
export class SpreadsheetBoardViewerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() board: UserBoard;
  @Input() filterBar: FilterBar;

  spreadsheetData: SpreadsheetBoardData | null = null;
  filtersApplied: any = {};
  isLoadingData: boolean = false;
  private filterSubscription: Subscription;
  private loadingInProgress: boolean = false;
  private lastLoadedBoardId: string = '';
  private lastLoadedFilters: string = '';

  constructor(
    private calcEngineService: CalcEngineService,
    private selectedFilterUpdateObserver: SelectedFilterUpdatesService
  ) {}

  ngOnInit(): void {
    // Subscribe to filter changes
    this.filterSubscription = this.selectedFilterUpdateObserver.filters$.subscribe((filters) => {
      this.filtersApplied = filters;
      this.loadSpreadsheetDataIfChanged();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Only load data if the board input actually changed
    if (changes['board'] && changes['board'].currentValue) {
      this.loadSpreadsheetDataIfChanged();
    }
  }

  ngOnDestroy(): void {
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }
  }

  private loadSpreadsheetDataIfChanged(): void {
    // Create unique identifiers to detect changes
    const currentBoardId = this.board?.userBoardId || '';
    const currentFilters = JSON.stringify(this.filtersApplied);
    
    // Check if we need to load (board changed or filters changed) and not already loading
    if (this.loadingInProgress) {
      return;
    }
    
    const boardChanged = currentBoardId !== this.lastLoadedBoardId;
    const filtersChanged = currentFilters !== this.lastLoadedFilters;
    
    if (!boardChanged && !filtersChanged) {
      return;
    }
    
    this.lastLoadedBoardId = currentBoardId;
    this.lastLoadedFilters = currentFilters;
    
    this.loadSpreadsheetData();
  }

  private loadSpreadsheetData(): void {
    if (this.board?.spreadsheetData) {
      // Load the spreadsheet data from the board
      this.spreadsheetData = {
        ...this.board.spreadsheetData,
        defaultRowHeight: 28,
        defaultColumnWidth: 100
      };

      // Load real data for cells that need it
      this.loadCellData();
    } else {
      // Handle legacy boards or empty boards
      this.spreadsheetData = null;
    }
  }

  private loadCellData(): void {
    if (!this.spreadsheetData || !this.spreadsheetData.cells) {
      return;
    }

    this.isLoadingData = true;
    this.loadingInProgress = true;
    const cellDataPromises: Promise<void>[] = [];

    // Process each cell to load data
    Object.keys(this.spreadsheetData.cells).forEach(cellKey => {
      const cell = this.spreadsheetData.cells[cellKey];

      if (cell.content.type === SpreadsheetCellType.Metric) {
        cellDataPromises.push(this.loadMetricCellData(cellKey, cell));
      } else if (cell.content.type === SpreadsheetCellType.Table) {
        cellDataPromises.push(this.loadTableCellData(cellKey, cell));
      }
    });

    // Wait for all data loading to complete
    Promise.all(cellDataPromises).finally(() => {
      this.isLoadingData = false;
      this.loadingInProgress = false;
    });
  }

  private async loadMetricCellData(cellKey: string, cell: any): Promise<void> {
    try {
      const request = new CalcEngineRequest();

      // Add the metric to the request
      if (cell.content.metric) {
        request.metricList.push({
          metricName: cell.content.metric.metricName,
          metricId: cell.content.metric.metricId
        } as Metric);
      }

      request.filters = this.filtersApplied;

      const response = await this.calcEngineService.runCalcEngine(request, false).toPromise();

      if (response && response.rowData && response.rowData.length > 0) {
        const rowData = response.rowData[0];
        const metricValue = rowData[cell.content.metric.metricName];

        // Update the cell with the real value
        if (this.spreadsheetData && this.spreadsheetData.cells[cellKey]) {
          this.spreadsheetData.cells[cellKey].content.metric.calculatedValue = metricValue;
        }
      }
    } catch (error) {
      console.error('Error loading metric data for cell', cellKey, error);
    }
  }

  private async loadTableCellData(cellKey: string, cell: any): Promise<void> {
    try {
      const request = new CalcEngineRequest();
      const table = cell.content.table;

      if (!table || !table.groupByField || !table.headers) {
        return;
      }

      const groupByValue = table.groupByField;
      request.groupBy.push(groupByValue);

      // Add metrics to the request
      table.headers.forEach(header => {
        if (header.type === 'metric' && header.metricId) {
          request.metricList.push({
            metricName: header.metricName,
            metricId: parseInt(header.metricId)
          } as Metric);
        }
      });

      request.filters = this.filtersApplied;

      const response = await this.calcEngineService.runCalcEngine(request, false).toPromise();

      if (response && response.rowData) {
        // Update the cell with the real table data
        if (this.spreadsheetData && this.spreadsheetData.cells[cellKey]) {
          this.spreadsheetData.cells[cellKey].content.table.calculatedData = response.rowData;
        }
      }
    } catch (error) {
      console.error('Error loading table data for cell', cellKey, error);
    }
  }

  hasContent(): boolean {
    return this.spreadsheetData &&
           this.spreadsheetData.cells &&
           Object.keys(this.spreadsheetData.cells).length > 0;
  }
}
